/**
 * 레거시 호환 — number 기반 K/M/B/T 표기/역파싱 (기존 app `util/bigNumber.ts` 흡수).
 * compactNumber: number → short-scale 문자열. parseSuffixNumber: 치트 입력 등 역파싱(→ number).
 * BigNumber 도입 후 표시는 format()으로 대체되나, 치트 입력 파서(parseSuffixNumber)는 존치.
 */

/** 접미사 → 배수 (오름차순). compactNumber는 큰 단위부터 역순 검사. */
export const SUFFIXES: ReadonlyArray<{ readonly symbol: string; readonly value: number }> = [
  { symbol: "K", value: 1_000 },
  { symbol: "M", value: 1_000_000 },
  { symbol: "B", value: 1_000_000_000 },
  { symbol: "T", value: 1_000_000_000_000 },
];

/**
 * 큰 수치를 compact 표기 (1000→1.0K, 1_000_000→1.0M, 1e12→1.0T 등).
 * 1000 미만은 정수 문자열. NaN/음수는 0 취급.
 */
export function compactNumber(n: number): string {
  if (!Number.isFinite(n) || n < 1_000) return String(Math.max(0, Math.floor(n) || 0));
  for (let i = SUFFIXES.length - 1; i >= 0; i--) {
    const s = SUFFIXES[i]!;
    if (n >= s.value) return (n / s.value).toFixed(1) + s.symbol;
  }
  return String(Math.floor(n));
}

/**
 * 접미사 표기 문자열 → 숫자. "1.5K"→1500, "2m"→2_000_000, "3B", "1t", "100"→100.
 * 대소문자 무관, 앞뒤 공백 trim, 천단위 콤마 허용. 잘못된 입력 → NaN (호출부에서 무시).
 */
export function parseSuffixNumber(input: string): number {
  const trimmed = input.trim().replace(/,/g, "");
  if (trimmed === "") return NaN;
  const m = /^(-?\d*\.?\d+)\s*([a-zA-Z]?)$/.exec(trimmed);
  if (!m) return NaN;
  const base = Number(m[1]);
  if (!Number.isFinite(base)) return NaN;
  const sym = m[2]!.toUpperCase();
  if (sym === "") return base;
  const found = SUFFIXES.find((s) => s.symbol === sym);
  return found ? base * found.value : NaN;
}
